/**
 * Add / edit a newsletter.
 *
 *  - Text and prayer requests -> TipTap rich text editors
 *  - Date  -> calendar dropdown (no free-text dates)
 *  - Partner / Country -> searchable dropdowns
 *  - AI    -> run a saved or ad-hoc prompt on the body or the prayer requests,
 *             or extract prayer requests out of the body into that field
 *  - File  -> import the text and the pictures out of a PDF or a photo of the page
 *
 * AI output always lands in the form for review; saving is a separate, explicit step.
 * Imported pictures are staged in `pictures` and uploaded only once the record has
 * an id to hang them on, which for a new newsletter is after the first save.
 */
import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../App'
import { api, useApi, useLookups } from '../lib/api'
import { base64ToBytes } from '../../shared/base64.js'
import { PartnerSelect, CountrySelect } from '../components/Select'
import { DateField, toIso } from '../components/DateField'
import { RichTextEditor, textToHtml, isBlankHtml } from '../components/RichText'
import { AiAssist, AiExtract, AiFileImport } from '../components/AiPanel'
import {
  ImageUpload, ImageStager, AttachedImages, MediaLightbox, stagedPicture,
} from '../components/ImageUpload'
import {
  Card, Field, Switch, Notice, Spinner, Badge, Loading, ErrorState,
  ConfirmModal, useToast, fmtDate, fmtBytes,
} from '../components/ui'
import { useCode } from '../lib/terms'
import { fundTypeLabel, secondaryKindsFor } from '../../shared/fundTypes.js'

/** base64 -> File, so a staged picture can go through the ordinary media upload. */
const toFile = (pic, index) => {
  const bytes = base64ToBytes(pic.data)
  const ext = (pic.mime_type || 'image/jpeg').split('/')[1] || 'jpg'
  const stem = (pic.source_name || 'import').replace(/\.[^.]+$/, '')
  return new File([bytes], `${stem}-${index + 1}.${ext}`, { type: pic.mime_type })
}

/* The reader's own today, not UTC's: in the evening in the Americas, UTC is already
   tomorrow, and a new update would have been dated a day ahead. */
const today = () => toIso(new Date())

/**
 * The tray of pictures waiting for a record to attach to — dropped on the form by
 * hand, or lifted out of an imported file. They are held as base64 rather than as
 * stored media, so the thumbnails read from the data itself — and so does the
 * lightbox, which is the only way to see what a thumbnail this small actually is
 * before deciding whether to keep it.
 */
const StagedPictures = ({ pictures, onDiscard, busy }) => {
  const [viewing, setViewing] = useState(null)

  return (
    <>
      <div className="media-grid">
        {pictures.map((pic, i) => {
          const shot = stagedPicture(
            pic,
            `Picture ${i + 1}${pic.source_name ? ` — ${pic.source_name}` : ''}`
          )
          return (
            <div className="media-item" key={`${pic.source_name}-${i}`}>
              <button
                type="button"
                className="media-open"
                onClick={() => setViewing(shot)}
                title="Open full size"
              >
                <img className="media-thumb" src={shot.src} alt={shot.alt_text} />
              </button>
              <div className="media-meta subtle truncate">
                {pic.width && pic.height ? `${pic.width}×${pic.height} · ` : ''}
                {fmtBytes(pic.bytes)}
              </div>
              <button className="btn btn-sm" onClick={() => onDiscard(i)} disabled={busy}>
                Discard
              </button>
            </div>
          )
        })}
      </div>

      <MediaLightbox image={viewing} onClose={() => setViewing(null)} />
    </>
  )
}

/**
 * Cached views of a newsletter, refreshed after a save. Without this the record
 * page the form navigates to would keep showing the copy it fetched before the
 * edit until the 30-second stale window passed.
 */
const STALE_ON_SAVE = [
  'newsletter', 'newsletters', 'partner-newsletters', 'prayer-requests', 'highlighted',
  'stats-overview', 'partner', 'search',
]

export default function NewsletterForm({ user, mode }) {
  const code = useCode()
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()
  const lookups = useLookups()
  const isEdit = mode === 'edit'
  // `?partner=<id>` — the New Update buttons on a partner unit's page and on the
  // updates report open the form with that unit already chosen. Its country follows
  // from the effect below that fills one in from the partner.
  const [params] = useSearchParams()
  const presetPartner = isEdit ? null : Number(params.get('partner')) || null

  const query = useApi(['newsletter', id], `newsletters/${id}`, { enabled: isEdit })

  const [form, setForm] = useState({
    partner_id: presetPartner,
    // The training school or project this update also reports on, if it does. Null is
    // the ordinary case — see the field itself, and migration 0032.
    secondary_partner_id: null,
    country_id: null,
    published_on: today(),
    body_html: '',
    prayer_requests_html: '',
    highlighted_need: false,
  })
  const [loaded, setLoaded] = useState(!isEdit)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState({})
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [dirty, setDirty] = useState(false)
  // Pictures accepted from a file import, waiting for a newsletter id to attach to.
  const [pictures, setPictures] = useState([])

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }))
    setDirty(true)
  }

  useEffect(() => {
    if (!isEdit || !query.data || loaded) return
    const n = query.data.newsletter
    setForm({
      partner_id: n.partner_id,
      secondary_partner_id: n.secondary_partner_id ?? null,
      country_id: n.country_id,
      published_on: n.published_on,
      body_html: n.body_html || '',
      // Records imported before prayer requests were rich text only have the
      // plain-text column; promote it so the editor opens on the same content.
      prayer_requests_html: n.prayer_requests_html || textToHtml(n.prayer_requests),
      highlighted_need: !!n.highlighted_need,
    })
    setLoaded(true)
    setDirty(false)
  }, [isEdit, query.data, loaded])

  // Default the newsletter's country to the partner's when creating a fresh record.
  useEffect(() => {
    if (isEdit || !form.partner_id || form.country_id) return
    const p = lookups.data?.partners?.find((x) => x.id === form.partner_id)
    if (p?.country_id) setForm((f) => ({ ...f, country_id: p.country_id }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.partner_id, isEdit, lookups.data])

  // Warn before losing unsaved edits.
  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const canWrite = isEdit ? user.caps.update : user.caps.create
  const partner = lookups.data?.partners?.find((p) => p.id === form.partner_id)
  const secondary = lookups.data?.partners?.find((p) => p.id === form.secondary_partner_id)
  /* What "Also about" may offer, which depends on who wrote it: a partner unit's update is
     about a training school or a project, and a school's or a project's may be about a
     partner unit too. The API applies the same rule — see `secondaryPartner` in the Worker
     — so this list is the convenience, not the rule. */
  const authorKind = partner?.fund_type ?? 'staff'
  const secondaryKinds = secondaryKindsFor(authorKind)

  const stagePictures = (picks) => {
    setPictures((prev) => [...prev, ...picks])
    setDirty(true)
  }

  const discardPicture = (i) => setPictures((prev) => prev.filter((_, j) => j !== i))

  /* A new update has no id for a picture to hang on, so it gets one card that both
     takes pictures and lists what is waiting. Anywhere else — editing, or a role that
     cannot upload — the tray below still lists what a file import staged. */
  const canStagePictures = !isEdit && canWrite && user.caps.uploadMedia

  const validate = () => {
    const e = {}
    if (!form.partner_id) e.partner_id = 'Choose the record this update came from'
    if (!form.published_on) e.published_on = 'A date is required'
    /* The picker cannot offer the author as the second record — it only holds training
       schools and projects — but it can be left holding one after the author is changed
       to it, which is the one way to reach this state. The server refuses it too; this is
       so the refusal arrives beside the field rather than as a red toast. */
    if (form.secondary_partner_id && form.secondary_partner_id === form.partner_id) {
      e.secondary_partner_id = 'This is already the record the update came from'
    } else if (secondary && !secondaryKinds.includes(secondary.fund_type ?? 'staff')) {
      // Reachable only by changing the author to a partner unit after linking one.
      e.secondary_partner_id = "A partner unit's update can only be about a training school or project"
    }
    if (isBlankHtml(form.body_html) && isBlankHtml(form.prayer_requests_html)) {
      e.body_html = 'Add update text or at least a prayer request'
    }
    setErrors(e)
    return !Object.keys(e).length
  }

  /**
   * Upload the staged pictures against a saved newsletter.
   *
   * A failure here must not read as "the newsletter did not save" — it did. So the
   * pictures that fail stay staged and are reported by name, and the caller carries
   * on to navigate away only when the tray is empty.
   */
  const uploadPictures = async (newsletterId) => {
    if (!pictures.length || !user.caps.uploadMedia) return pictures.length
    const failed = []
    for (const [i, pic] of pictures.entries()) {
      try {
        const fd = new FormData()
        fd.append('file', toFile(pic, i))
        fd.append('kind', 'newsletter_image')
        fd.append('newsletter_id', String(newsletterId))
        if (user.caps.useAi) fd.append('describe', '1')
        await api('media', { method: 'POST', formData: fd })
      } catch (err) {
        failed.push({ pic, message: err.message })
      }
    }
    setPictures(failed.map((f) => f.pic))
    if (failed.length) {
      toast.error(
        `The update saved, but ${failed.length} of ${pictures.length} pictures did not ` +
          `upload (${failed[0].message}). They are still listed on the form.`
      )
    } else {
      toast.ok(`${pictures.length} picture${pictures.length === 1 ? '' : 's'} attached`)
    }
    return failed.length
  }

  const refreshViews = () => {
    for (const key of STALE_ON_SAVE) qc.invalidateQueries({ queryKey: [key] })
  }

  const save = async (andNew = false) => {
    if (!validate()) {
      toast.error('Please fix the highlighted fields')
      return
    }
    setBusy(true)
    try {
      if (isEdit) {
        await api(`newsletters/${id}`, { method: 'PUT', body: form })
        toast.ok('Update saved')
        setDirty(false)
        refreshViews()
        const stillStaged = await uploadPictures(id)
        if (stillStaged) {
          setBusy(false)
          return
        }
        navigate(`/newsletters/${id}`)
      } else {
        const res = await api('newsletters', { method: 'POST', body: form })
        toast.ok('Update created')
        setDirty(false)
        refreshViews()
        const stillStaged = await uploadPictures(res.id)
        if (stillStaged) {
          setBusy(false)
          return
        }
        if (andNew) {
          // A fresh form, not a copy of the one just saved: the partner unit has to
          // be chosen again so the next update cannot inherit the previous one's
          // code by accident, and the date goes back to today rather than to
          // whatever date the last record was published on.
          setForm({
            partner_id: null,
            secondary_partner_id: null,
            country_id: null,
            published_on: today(),
            body_html: '',
            prayer_requests_html: '',
            highlighted_need: false,
          })
          setErrors({})
        } else {
          navigate(`/newsletters/${res.id}`)
        }
      }
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  /** Where Cancel goes back to: the record being edited, or the list for a new one. */
  const leaveTo = isEdit ? `/newsletters/${id}` : '/newsletters'

  const cancel = () => {
    if (dirty) setConfirmCancel(true)
    else navigate(leaveTo)
  }

  const remove = async () => {
    setBusy(true)
    try {
      await api(`newsletters/${id}`, { method: 'DELETE' })
      toast.ok('Update deleted')
      navigate('/newsletters')
    } catch (err) {
      toast.error(err)
      setBusy(false)
    }
  }

  if (isEdit && query.isLoading) {
    return (
      <PageHeader title="Edit Update">
        <Loading />
      </PageHeader>
    )
  }
  if (isEdit && query.isError) {
    return (
      <PageHeader title="Edit Update">
        <ErrorState error={query.error} retry={query.refetch} />
      </PageHeader>
    )
  }

  return (
    <PageHeader
      title={isEdit ? `Edit Update — ${fmtDate(form.published_on)}` : 'New Update'}
      sub={
        isEdit
          ? `${code(query.data?.newsletter?.s4ca_code)} · record #${id}`
          : 'Rich text, AI assistance and prayer request extraction'
      }
      actions={
        <>
          {isEdit && <Link className="btn btn-sm" to={`/newsletters/${id}`}>View</Link>}
          <button className="btn btn-sm" onClick={cancel} disabled={busy}>
            Cancel
          </button>
        </>
      }
    >
      {!canWrite && (
        <Notice tone="warn" icon="lock" title="Read-only">
          Your role ({user.roleLabel}) cannot {isEdit ? 'edit' : 'create'} updates. The form
          below is disabled.
        </Notice>
      )}

      <div className="col" style={{ marginTop: canWrite ? 0 : 16 }}>
        <Card title="Record">
          <div className="field-row">
            {/* "Record" rather than "Partner Unit": since migration 0031 an update can be
                filed against a training school or a project as well, and the picker marks
                which is which. The code is still how one is found, hence the hint. */}
            <Field
              label="S4CA Record"
              hint="A partner unit, training school or project"
              required
              error={errors.partner_id}
              htmlFor="nf-partner"
            >
              <PartnerSelect
                inputId="nf-partner"
                value={form.partner_id}
                onChange={(v) => set({ partner_id: v })}
                isDisabled={!canWrite}
              />
            </Field>
            <Field
              label="Date"
              required
              error={errors.published_on}
              hint="Pick from the calendar"
              htmlFor="nf-date"
            >
              <DateField
                id="nf-date"
                value={form.published_on}
                onChange={(v) => set({ published_on: v })}
                disabled={!canWrite}
                isClearable={false}
              />
            </Field>
            <Field
              label="Country"
              hint={
                partner?.country_name && form.country_id !== partner.country_id
                  ? `Partner unit's registered country is ${partner.country_name}`
                  : 'Where this update was written from'
              }
              htmlFor="nf-country"
            >
              <CountrySelect
                inputId="nf-country"
                value={form.country_id}
                onChange={(v) => set({ country_id: v })}
                isDisabled={!canWrite}
              />
            </Field>
          </div>
          {/* The second record, on its own row rather than as a fourth box beside the
              first three. Two reasons: the row above is what every update needs and this
              is what some do, and a picker squeezed to a quarter width is a picker nobody
              can read a code in. */}
          <div className="field-row" style={{ marginTop: 16 }}>
            <Field
              label="Also about"
              hint={
                secondary
                  ? `Filed against ${fundTypeLabel(secondary.fund_type).toLowerCase()} ` +
                    `${code(secondary.s4ca_code)} as well — it will appear on that record too`
                  : authorKind === 'staff'
                    ? 'Optional — a training school or project this update also reports on'
                    : 'Optional — a partner unit, training school or project this update also reports on'
              }
              error={errors.secondary_partner_id}
              htmlFor="nf-secondary"
            >
              <PartnerSelect
                inputId="nf-secondary"
                value={form.secondary_partner_id}
                onChange={(v) => set({ secondary_partner_id: v })}
                fundType={secondaryKinds}
                placeholder={
                  authorKind === 'staff'
                    ? 'Nothing else — this update is about the unit alone'
                    : `Nothing else — this update is about the ${fundTypeLabel(authorKind).toLowerCase()} alone`
                }
                isDisabled={!canWrite}
              />
            </Field>
          </div>

          <div style={{ marginTop: 16 }}>
            <Switch
              checked={form.highlighted_need}
              onChange={(v) => set({ highlighted_need: v })}
              label="Highlighted need"
              disabled={!canWrite}
            />
          </div>
        </Card>

        <Card
          title="Update Text"
          sub="Rich text — bold, lists, headings and links are preserved"
          actions={
            user.caps.useAi && canWrite ? (
              <>
                <AiFileImport
                  currentBody={form.body_html}
                  onAccept={(html) => set({ body_html: html })}
                  onPictures={stagePictures}
                  entityId={isEdit ? Number(id) : null}
                  maxBytes={user.maxImportBytes}
                />
                <AiAssist
                  fieldKey="newsletters.body_html"
                  fieldLabel="Update text"
                  value={form.body_html}
                  onAccept={(html) => set({ body_html: html })}
                  entity="newsletter"
                  entityId={isEdit ? Number(id) : null}
                />
              </>
            ) : null
          }
        >
          {errors.body_html && <div className="field-error" style={{ marginBottom: 8 }}>{errors.body_html}</div>}
          <RichTextEditor
            value={form.body_html}
            onChange={(html) => set({ body_html: html })}
            placeholder="Paste or write the update here…"
          />
        </Card>

        <Card
          title="Prayer Requests"
          sub="Rich text — one request per line, or use a bulleted or numbered list"
          actions={
            <>
              {user.caps.useAi && canWrite && (
                <AiExtract
                  bodyHtml={form.body_html}
                  currentPrayer={form.prayer_requests_html}
                  onAccept={(html) => set({ prayer_requests_html: html })}
                  entityId={isEdit ? Number(id) : null}
                />
              )}
              {user.caps.useAi && canWrite && (
                <AiAssist
                  fieldKey="newsletters.prayer_requests"
                  fieldLabel="Prayer Requests"
                  value={form.prayer_requests_html}
                  onAccept={(html) => set({ prayer_requests_html: html })}
                  entity="newsletter"
                  entityId={isEdit ? Number(id) : null}
                  buttonLabel="AI tidy"
                />
              )}
            </>
          }
        >
          <RichTextEditor
            value={form.prayer_requests_html}
            onChange={(html) => set({ prayer_requests_html: html })}
            placeholder="Pray for …"
            small
            footer={
              isBlankHtml(form.prayer_requests_html) && !isBlankHtml(form.body_html) ? (
                <span>Use “Extract prayer requests” to pull them out of the update text.</span>
              ) : null
            }
          />
        </Card>

        {canStagePictures && (
          <Card
            title={pictures.length ? `Pictures — ${pictures.length} Waiting` : 'Pictures'}
            sub={
              'Uploaded once the update is created, so there is a record to attach them to. ' +
              (user.caps.useAi
                ? 'Alt text is written for each one on upload, and can be corrected afterwards.'
                : 'Alt text can be added once the update exists.')
            }
            actions={
              pictures.length ? (
                <button className="btn btn-sm" onClick={() => setPictures([])} disabled={busy}>
                  Discard all
                </button>
              ) : null
            }
          >
            <ImageStager
              maxBytes={user.maxImageBytes}
              onStage={stagePictures}
              disabled={busy}
            />
            {pictures.length > 0 && (
              <StagedPictures pictures={pictures} onDiscard={discardPicture} busy={busy} />
            )}
          </Card>
        )}

        {pictures.length > 0 && !canStagePictures && (
          <Card
            title={`Pictures from the Import — ${pictures.length} Waiting`}
            sub={user.caps.uploadMedia ? 'Uploaded when you save this update' : undefined}
            actions={
              <button className="btn btn-sm" onClick={() => setPictures([])} disabled={busy}>
                Discard all
              </button>
            }
          >
            {!user.caps.uploadMedia ? (
              <Notice tone="warn" icon="lock" title="These cannot be attached">
                Your role ({user.roleLabel}) cannot upload images, so these pictures will be
                dropped when you leave the page. The update text is unaffected.
              </Notice>
            ) : null}
            <StagedPictures pictures={pictures} onDiscard={discardPicture} busy={busy} />
          </Card>
        )}

        {isEdit && user.caps.uploadMedia && (
          <Card
            title="Images"
            sub="Resized below 300 KB before storage. Click a picture to see it full size."
          >
            <div className="grid grid-2">
              <ImageUpload
                kind="newsletter_image"
                newsletterId={Number(id)}
                maxBytes={user.maxImageBytes}
                describe={user.caps.useAi}
                onUploaded={() => query.refetch()}
                label="Attach an image to this update"
              />
              {/*
                Alt text is edited here, on the stored image, rather than only in
                the uploader: the upload starts the instant a file is chosen, so
                anything typed into the uploader's field after that point was
                never sent.
              */}
              <AttachedImages
                images={query.data?.images}
                user={user}
                onChanged={() => query.refetch()}
              />
            </div>
          </Card>
        )}

        <div className="row" style={{ paddingBottom: 24 }}>
          <button className="btn btn-primary btn-lg" onClick={() => save(false)} disabled={busy || !canWrite}>
            {busy ? <Spinner /> : isEdit ? 'Save changes' : 'Create update'}
          </button>
          {!isEdit && (
            <button className="btn btn-lg" onClick={() => save(true)} disabled={busy || !canWrite}>
              Save & Add Another
            </button>
          )}
          {/*
            A real button rather than a <Link>: leaving with unsaved edits should
            ask first, and the browser's own beforeunload prompt does not fire on
            a client-side route change.
          */}
          <button className="btn btn-lg" onClick={cancel} disabled={busy}>
            Cancel
          </button>
          {dirty && <Badge tone="warn">unsaved changes</Badge>}
          <span className="spacer" />
          {isEdit && user.caps.delete && (
            <button className="btn btn-danger" onClick={() => setConfirmDelete(true)} disabled={busy}>
              Delete
            </button>
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={() => {
          setDirty(false)
          navigate(leaveTo)
        }}
        title="Discard your changes?"
        confirmLabel="Discard changes"
        body={
          <p>
            The edits on this form have not been saved
            {pictures.length ? `, and ${pictures.length} imported picture${pictures.length === 1 ? '' : 's'} would be dropped` : ''}
            . Images you have already attached are stored and are not affected.
          </p>
        }
      />

      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        busy={busy}
        title="Delete this update?"
        body={
          <p>
            The update dated <strong>{fmtDate(form.published_on)}</strong> will be permanently
            removed. This cannot be undone, though the deletion is recorded in the audit log.
          </p>
        }
      />
    </PageHeader>
  )
}
